// Axios-клиент с request/response interceptors для JWT-аутентификации.
// - Request interceptor: подставляет `Authorization: Bearer <accessToken>` если токен есть.
// - Response interceptor: на 401 вызывает `POST /api/auth/refresh`, сохраняет новые токены
//   и повторяет оригинальный запрос. При refresh failure — clearTokens + onUnauthorized callback.
//
// Concurrent 401 protection: при одновременных 401 на разных endpoints — только один refresh
// (флаг `isRefreshing` + очередь `failedQueue`).

import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { jwtStorage } from '@/auth/jwtStorage';

// Базовый URL для API.
// По умолчанию — пустая строка (относительный путь), чтобы Vite dev proxy мог
// перехватывать /api/* запросы в dev-режиме. В production задаётся через VITE_API_BASE_URL.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Callback вызывается, когда refresh token истёк → пользователь должен быть отправлен на /login. */
type OnUnauthorized = () => void;

let onUnauthorizedCallback: OnUnauthorized = () => undefined;

/** Установить callback (вызывается из AuthContext). */
export function setOnUnauthorized(callback: OnUnauthorized): void {
  onUnauthorizedCallback = callback;
}

interface RetryConfig extends AxiosRequestConfig {
  _isRetry?: boolean;
}

interface FailedRequest {
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}

let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

/** Обработка очереди failed requests после успешного refresh. */
function processQueue(error: AxiosError | null, newAccessToken: string | null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(newAccessToken);
    }
  });
  failedQueue = [];
}

/** Axios instance для всех API-запросов к backend «Упряжка». */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor — attach JWT
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = jwtStorage.getAccessToken();
    if (token && config.headers) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor — auto-refresh on 401
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined;
    const status = error.response?.status;

    // Не 401, или это refresh сам упал, или уже retry — пробрасываем ошибку
    if (status !== 401 || !originalRequest || originalRequest._isRetry || originalRequest.url?.endsWith('/api/auth/refresh')) {
      return Promise.reject(error);
    }

    // Если refresh уже идёт — ставим запрос в очередь
    if (isRefreshing) {
      return new Promise<unknown>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._isRetry = true;
    isRefreshing = true;

    const refreshToken = jwtStorage.getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      jwtStorage.clearTokens();
      onUnauthorizedCallback();
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
        `${API_BASE_URL}/api/auth/refresh`,
        { refreshToken },
        { headers: { 'Content-Type': 'application/json' } },
      );
      jwtStorage.saveTokens(data.accessToken, data.refreshToken);
      processQueue(null, data.accessToken);
      isRefreshing = false;
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError as AxiosError, null);
      isRefreshing = false;
      jwtStorage.clearTokens();
      onUnauthorizedCallback();
      return Promise.reject(refreshError);
    }
  },
);
